import { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { SessionContext } from '../../context/SessionContext';
import { getAllUsersInSession } from '../../service/userService';
import { getTelemetryEventsBySessionAndUser } from '../workspace/service/telemetryService';
import { isGuiSession } from '../workspace/config/guiSessionConfig';
import { isReasoningGapSession } from '../reasoningGap/reasoningGapConfig';
import { loadReasoningGapStudentData } from '../reasoningGap/reasoningGapStudentData';
import StudentGridView from './StudentGridView';
import StudentDetailView from './StudentDetailView';

const RawDataModal = ({ open, onClose }) => {
  const { session } = useContext(SessionContext);
  const [view, setView] = useState('students');
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  // Reasoning Gap pages log their own event types and keep chat text, test results and hints
  // in their own tables; these are loaded alongside each telemetry fetch (so both are equally
  // fresh during a live quiz) and adapt the events to what the tabs and replay below read.
  const reasoningGap = isReasoningGapSession(session);
  const sessionId = session?.id;
  const [replayOptions, setReplayOptions] = useState(null);
  const getReasoningGapData = useCallback(() => loadReasoningGapStudentData(sessionId), [sessionId]);
  const loadEventAdapter = useMemo(
    () => (reasoningGap ? () => getReasoningGapData().then(data => data.adapt) : undefined),
    [reasoningGap, getReasoningGapData]
  );
  const [studentData, setStudentData] = useState({
    interactions: [],
    messages: [],
    testRuns: [],
    codeSubmissions: [],
    terminalOutput: [],
    typingActivity: [],
    rawEvents: [],
    allEvents: []
  });

  // Fetch all students when modal opens
  useEffect(() => {
    if (open && session?.id) {
      fetchStudents();
      // Reset view when modal opens
      setView('students');
      setSelectedStudent(null);
    }
  }, [open, session?.id]);

  const fetchStudents = async () => {
    try {
      const users = await getAllUsersInSession(session.id);
      // role 3 = anonymous guest, role 4 = a real, self-registered student account.
      const studentUsers = users.filter(u => u.role === 3 || u.role === 4);
      setStudents(studentUsers);
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  };

  // Sourced from telemetry_events (the pipeline the workspace actually writes to) rather
  // than the interaction/message/submission/rrweb_events tables this used to read - those
  // stopped being written on 2026-08-21/22 when the app moved to batched telemetry events,
  // so every one of those queries silently returned nothing for any current session
  // (confirmed: e.g. session 719 has 0 interaction/submission/rrweb_events rows but 24
  // telemetry_events batches). Field shapes below match ConversationsModal.jsx's existing
  // event-derivation conventions (sender_id -4 = AI, payload field names) for consistency.
  // There's no current equivalent for the old LLM-intent classification/rating pipeline
  // (question_type, student_intent, intent_rating) or per-keystroke/rrweb capture - those
  // were part of a specific past study's instrumentation, not part of what any session
  // collects today, so `interactions` stays empty rather than being faked.
  const fetchStudentData = async (student) => {
    try {
      const [rawEvents, reasoningGapData] = await Promise.all([
        getTelemetryEventsBySessionAndUser(session.id, student.id),
        reasoningGap
          ? getReasoningGapData().catch((error) => {
            console.error('Error loading Reasoning Gap submissions for the replay:', error);
            return null;
          })
          : null,
      ]);
      const events = reasoningGapData ? reasoningGapData.adapt(rawEvents, student.id) : rawEvents;
      setReplayOptions(reasoningGapData ? reasoningGapData.replayOptions : null);

      const messages = events
        .filter(e => e.type === 'CHAT_QUERY' || e.type === 'CHAT_RESPONSE')
        .map(e => ({
          sender_id: e.type === 'CHAT_QUERY' ? student.id : -4,
          content: e.payload?.text || e.payload?.content || e.payload?.message || '',
          created_time: new Date(e.timestamp),
        }));

      // The `type?.startsWith('CODE_')` clause used to also require this, but nothing with a
      // CODE_* type has ever carried a full code snapshot - CODE_TYPE/CODE_DELETE etc. only
      // carry per-keystroke deltas (see the typing-activity reconstruction below). The actual
      // snapshot events are SESSION_EVENTS.TASK_COMPLETED/TASK_TIMEOUT (logged in
      // Workspace.jsx) and, as of the TERMINAL_EVENTS.RUN payload fix in TerminalContext.js,
      // every test run - none of those type strings start with 'CODE_', so the old filter
      // silently matched nothing and every code_submission's `content` came back ''.
      // payload.code being present is already a precise enough signal on its own.
      const codeEventsWithSnapshot = events.filter(
        e => e.payload?.code !== undefined
      );
      const codeAtOrBefore = (timestamp) => {
        const prior = codeEventsWithSnapshot.filter(e => e.timestamp <= timestamp);
        return prior.length > 0 ? prior[prior.length - 1].payload.code : '';
      };
      const errorNear = (timestamp) => {
        // Returning {} for "no error" made StudentDetailView's `testRun.error &&` check
        // always truthy (an empty object is truthy), so every test run showed an "Error: {}"
        // box regardless of whether one actually occurred. null is the real "no error" value.
        const prior = events.filter(e => e.type === 'TERMINAL_ERROR' && e.timestamp <= timestamp);
        if (prior.length === 0) return null;
        const last = prior[prior.length - 1];
        if (timestamp - last.timestamp > 30000) return null;
        return { type: last.payload?.type, message: last.payload?.message };
      };

      const testResultEvents = events.filter(e => e.type === 'TEST_CASE_RESULT');
      const studentTestRuns = testResultEvents.map(e => ({
        created_time: new Date(e.timestamp),
        result_list: e.payload?.detailed_results
          ? e.payload.detailed_results.map(r => r.passed)
          : Array.from({ length: e.payload?.total_tests || 0 }, (_, i) => i < (e.payload?.passed_count || 0)),
        error: errorNear(e.timestamp),
      }));

      // "Code submitted" = the code state at each attempt to run it (TERMINAL_RUN), not
      // only at a formal TEST_CASE_RESULT - a session with no test suite (e.g. a plain
      // "print Hello World" exercise, which is what exposed this: John Student's session
      // had 6 TERMINAL_RUN events and zero TEST_CASE_RESULT ones) still has real code to
      // show. Pass rate is attached only when a test result landed within 5s of the run.
      const passrateNear = (timestamp) => {
        const nearby = testResultEvents.filter(e => Math.abs(e.timestamp - timestamp) <= 5000);
        return nearby.length > 0 ? nearby[nearby.length - 1].payload?.passrate ?? null : null;
      };
      const studentCode = events
        .filter(e => e.type === 'TERMINAL_RUN')
        .map(e => ({
          created_time: new Date(e.timestamp),
          // ranCode: the code a run executed, when an adapter knows it (Reasoning Gap).
          content: e.payload?.ranCode ?? codeAtOrBefore(e.timestamp),
          passrate: passrateNear(e.timestamp),
        }));

      const terminalOutput = events
        .filter(e => e.type === 'TERMINAL_OUTPUT' || e.type === 'TERMINAL_ERROR')
        .map(e => ({
          timestamp: new Date(e.timestamp),
          text: e.type === 'TERMINAL_ERROR'
            ? `${e.payload?.type || 'Error'}: ${e.payload?.message || ''}`
            : (e.payload?.output || ''),
        }))
        .filter(t => t.text.trim().length > 0);

      const studentInteractions = [];

      // Per-keystroke typing activity. CODE_TYPE/CODE_DELETE carry a CodeMirror-style change
      // delta ({from, to, text}) - a real per-keystroke diff, safe to use as-is. CHAT_TYPE is a
      // different shape entirely: payload.text is the FULL current input value at that
      // instant, not a delta ("W", then "Di", then "Did", then "Did " ...) - treating it as a
      // single "key" (the original version of this code did, via the `e.payload?.text`
      // fallback below) meant every subsequent keystroke's ever-growing cumulative string got
      // logged and then concatenated as if each were its own keypress, producing exactly the
      // repeating-prefix garbage reported: "WDDiDidDid Did IDid I...". Recovered the actual
      // per-keystroke delta by diffing each CHAT_TYPE's text against the running cumulative
      // value from the previous chat-typing event, also folding in CHAT_DELETE (previously
      // excluded from this reconstruction entirely, unlike CODE_DELETE for code) so a
      // backspace mid-message keeps the cumulative tracker in sync instead of producing a
      // spurious non-contiguous diff at the following CHAT_TYPE event.
      const toKeystrokes = (typeEvents) => {
        let lastTs = null;
        let lastChatText = null;
        return typeEvents.map(e => {
          const change = e.payload?.changes?.[0];
          let key;
          if (e.type === 'CHAT_DELETE') {
            key = e.payload?.key_used || 'Backspace';
            lastChatText = e.payload?.text_after ?? lastChatText;
          } else if (change || e.type === 'CODE_TYPE' || e.type === 'CODE_DELETE') {
            const isDeletion = e.type === 'CODE_DELETE' || (change && change.to > change.from && !change.text);
            key = isDeletion ? 'Backspace' : (change?.text ?? '');
          } else if (typeof e.payload?.text === 'string') {
            const current = e.payload.text;
            const previous = lastChatText ?? '';
            if (current.length > previous.length && current.startsWith(previous)) {
              key = current.slice(previous.length);
            } else if (current.length < previous.length && previous.startsWith(current)) {
              key = 'Backspace';
            } else {
              // Non-contiguous change (e.g. a paste replacing a selection) - show the
              // resulting value rather than guessing at a delta.
              key = current;
            }
            lastChatText = current;
          } else {
            key = '';
          }
          const timeDifference = lastTs != null ? e.timestamp - lastTs : 0;
          lastTs = e.timestamp;
          return { key, timeDifference, timestamp: e.timestamp };
        });
      };

      const codeTypingByFile = {};
      events
        .filter(e => e.type === 'CODE_TYPE' || e.type === 'CODE_DELETE')
        .sort((a, b) => a.timestamp - b.timestamp)
        .forEach(e => {
          const file = e.payload?.active_file || 'code';
          if (!codeTypingByFile[file]) codeTypingByFile[file] = [];
          codeTypingByFile[file].push(e);
        });

      const typingActivity = Object.entries(codeTypingByFile).map(([file, fileEvents]) => ({
        type: 'code',
        label: file,
        created_time: new Date(fileEvents[0].timestamp),
        keystrokes: toKeystrokes(fileEvents),
      }));

      const chatTypeEvents = events
        .filter(e => e.type === 'CHAT_TYPE' || e.type === 'CHAT_DELETE')
        .sort((a, b) => a.timestamp - b.timestamp);
      if (chatTypeEvents.length > 0) {
        typingActivity.push({
          type: 'chat',
          label: 'chat',
          created_time: new Date(chatTypeEvents[0].timestamp),
          keystrokes: toKeystrokes(chatTypeEvents),
        });
      }

      const allEvents = [
        ...messages.map(m => ({ ...m, type: 'message', timestamp: m.created_time })),
        ...studentTestRuns.map(s => ({ ...s, type: 'test_run', timestamp: s.created_time })),
        ...studentCode.map(c => ({ ...c, type: 'code_submission', timestamp: c.created_time })),
        ...terminalOutput.map(t => ({ ...t, type: 'terminal_output' })),
      ].sort((a, b) => a.timestamp - b.timestamp);

      setStudentData({
        interactions: studentInteractions,
        messages: messages,
        testRuns: studentTestRuns,
        codeSubmissions: studentCode,
        terminalOutput: terminalOutput,
        typingActivity: typingActivity,
        rawEvents: events,
        allEvents: allEvents
      });
    } catch (error) {
      console.error('Error fetching student data:', error);
    }
  };

  const handleStudentClick = async (student) => {
    setSelectedStudent(student);
    setView('student-detail');
    await fetchStudentData(student);
  };

  const handleBackToStudents = () => {
    setView('students');
    setSelectedStudent(null);
    setStudentData({ 
      interactions: [],
      messages: [],
      testRuns: [],
      codeSubmissions: [],
      terminalOutput: [],
      typingActivity: [],
      rawEvents: [],
      allEvents: []
    });
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '80vh',
          maxHeight: '80vh'
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {view === 'students' ? 'Student Data' : 'Student Activity'}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {view === 'students' ? (
          <StudentGridView 
            students={students}
            onStudentClick={handleStudentClick}
            onStudentsUpdate={fetchStudents}  
            loadEventAdapter={loadEventAdapter}
          />
        ) : (
          <StudentDetailView
            student={selectedStudent}
            students={students}
            studentData={studentData}
            sessionId={session?.id}
            isGuiSession={isGuiSession(session)}
            replayOptions={replayOptions}
            onBack={handleBackToStudents}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RawDataModal;